import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import Loading from '../../Shared/Loading';
import { RootState } from '../../../reducers';
import { fetchMemberAccessReport, exportMemberAccessReport } from '../../../store/memberAccessReport.slice';
import { parentUrl } from '../../../utils/constants';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { isValid } from 'date-fns';
import { formatDate } from '../../../utils/dateHelpers';

const dateFilterList = [
    { Id: TimePeriodFilterEnum.Last1Month, Value: TimePeriodFilterEnum.Last1MonthDescription },
    { Id: TimePeriodFilterEnum.Last3Months, Value: TimePeriodFilterEnum.Last3MonthsDescription },
    { Id: TimePeriodFilterEnum.Last6Months, Value: TimePeriodFilterEnum.Last6MonthsDescription },
    { Id: TimePeriodFilterEnum.Last1Year, Value: TimePeriodFilterEnum.Last1YearDescription },
    { Id: TimePeriodFilterEnum.Custom, Value: TimePeriodFilterEnum.CustomDescription },
];

const MemberAccessReport = () => {
    const dispatch = useAppDispatch();
    const { memberAccessData } = useSelector((state: RootState) => state.memberAccessReport);
    const { downLoading } = useSelector((state: RootState) => state.shared);
    const [searchKey, setSearchKey] = useState('');
    const [dateFilter, setDateFilter] = useState(TimePeriodFilterEnum.Last1Month);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [paginationModel, setPaginationModel] = useState({ pageSize: 15, page: 0 });

    const columns: GridColDef[] = [
        {
            field: 'AHCCCSId',
            headerName: 'AHCCCS_ID',
            minWidth: 120,
        },
        {
            field: 'MemberName',
            headerName: 'Member Name',
            minWidth: 200,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 120,
        },
        {
            field: 'CreationTime',
            headerName: 'Accessed On',
            minWidth: 200,
            renderCell: (params) => {
                const date = params.row.CreationTime ? new Date(params.row.CreationTime) : null;

                return <Typography>{date && isValid(date) ? formatDate(date, 'MM/dd/yyyy hh:mm a') : '-'}</Typography>;
            },
        },
        {
            field: 'ProviderName',
            headerName: 'Accessed By',
            minWidth: 180,
        },
        {
            field: 'Type',
            headerName: 'Type',
            minWidth: 170,
        },
        {
            field: 'Description',
            headerName: 'Description',
            minWidth: 500,
        },
    ];

    const changeFromDate = (date: any) => {
        setFromDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(toDate) || new Date(toDate) <= dateInfo) {
            let endDate = dateInfo.setDate(dateInfo.getDate() + 30);
            setToDate(endDate);
        }
    };

    const changeToDate = (date: any) => {
        setToDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(fromDate) || new Date(fromDate) >= dateInfo) {
            let startDate = dateInfo.setDate(dateInfo.getDate() - 30);
            setFromDate(startDate);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box style={{ width: '100%', marginLeft: '8px' }} component={'div'}>
                <Grid container justifyContent={'flex-end'} spacing={3} style={{ padding: '16px 16px 16px 16px' }}>
                    <Box width={'100%'} borderBottom={1} paddingBottom={1}>
                        <Typography fontWeight="fontWeightMedium" component="h3">
                            Member Access Report
                        </Typography>
                    </Box>
                    {<Loading message={'Loading...'} />}
                    <Grid size={12}>
                        <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={3} paddingY={1}>
                            <TextField
                                size="small"
                                sx={{ width: 200 }}
                                label="AHCCCS ID"
                                value={searchKey}
                                onChange={(event) => setSearchKey(event?.target?.value)}
                                placeholder="Enter AHCCCS ID"
                            />
                            <FormControl size="small" sx={{ width: 200 }}>
                                <InputLabel>Date Filter</InputLabel>
                                <Select value={dateFilter} label="Date Filter" name="Date" onChange={(e) => setDateFilter(parseInt(e.target.value as any))}>
                                    {dateFilterList.map((date) => (
                                        <MenuItem key={date.Id} value={date.Id}>
                                            {date.Value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {dateFilter === TimePeriodFilterEnum.Custom ? (
                                <React.Fragment>
                                    <DesktopDatePicker
                                        label="From Date"
                                        value={fromDate || null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeFromDate(newValue)}
                                        slotProps={{ textField: { size: 'small', sx: { width: 200 } } }}
                                    />
                                    <DesktopDatePicker
                                        label="To Date"
                                        value={toDate || null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeToDate(newValue)}
                                        slotProps={{ textField: { size: 'small', sx: { width: 200 } } }}
                                    />
                                </React.Fragment>
                            ) : null}
                            <Button
                                onClick={() =>
                                    dispatch(
                                        fetchMemberAccessReport(
                                            searchKey,
                                            dateFilter,
                                            Boolean(fromDate) && dateFilter === TimePeriodFilterEnum.Custom ? new Date(fromDate).toISOString() : '',
                                            Boolean(toDate) && dateFilter === TimePeriodFilterEnum.Custom ? new Date(toDate).toISOString() : ''
                                        )
                                    )
                                }
                                style={{ minWidth: '120px' }}
                                variant="contained"
                            >
                                Search
                            </Button>
                            <Button
                                style={{ minWidth: '120px', marginLeft: 'auto' }}
                                onClick={() =>
                                    dispatch(
                                        exportMemberAccessReport(
                                            searchKey,
                                            dateFilter,
                                            Boolean(fromDate) && dateFilter === TimePeriodFilterEnum.Custom ? new Date(fromDate).toISOString() : '',
                                            Boolean(toDate) && dateFilter === TimePeriodFilterEnum.Custom ? new Date(toDate).toISOString() : ''
                                        )
                                    )
                                }
                                variant="contained"
                                color="success"
                            >
                                {downLoading ? 'Downloading..' : 'Export'}
                            </Button>
                        </Stack>
                    </Grid>
                    <Grid width={'100%'}>
                        <div style={{ height: '75vh', width: '100%' }}>
                            <DataGrid
                                rows={memberAccessData}
                                columns={columns}
                                pagination
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                pageSizeOptions={[5]}
                                getRowId={(row) => Math.random()}
                                getRowHeight={() => 'auto'}
                                onRowClick={(params) => {
                                    window.open(`${parentUrl}provider/members-list/member-chart/${params.row.MemberId}/member-profile`, '_blank');
                                }}
                                sx={{
                                    [`& .${gridClasses.cell}`]: {
                                        py: 2,
                                    },
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
            </Box>
        </LocalizationProvider>
    );
};

export default MemberAccessReport;
